import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { usePersonPhoto } from '../lib/person-photo'
import { useLang } from '../i18n'
import { IconClose } from './NavIcons'
import type { DirectoryPerson } from '../lib/types'

/**
 * Pick a colleague by name or corporate address.
 *
 * The EMAIL is the value — a bid roster is keyed by address, and the display name is decoration.
 * `onChange` therefore always reports an address, with the name alongside only so the caller can
 * show it while the person has never signed in.
 *
 * Two things this component takes seriously:
 *
 *  - **It is a real combobox.** role="combobox" + role="listbox"/option, aria-activedescendant,
 *    Up/Down/Enter/Escape, and selection on `onMouseDown` so a click beats the input's blur. The
 *    reference implementation this mirrors used plain divs with no roles and no keyboard handling,
 *    which is unusable with a screen reader and fails Niobe's WCAG 2.2 AA rule.
 *  - **Degradation is a first-class path, not an error state.** The directory lookup needs a Graph
 *    permission that may never be consented in this tenant, and the backend answers an EMPTY LIST
 *    rather than failing. So a well-formed corporate address typed by hand is always accepted, with
 *    or without a directory hit. An empty result never means "no such person".
 */

/** A deterministic avatar tint, so the same person is the same colour everywhere. */
function hueFor(email: string): number {
  let h = 0
  for (let i = 0; i < email.length; i += 1) h = (h * 31 + email.charCodeAt(i)) % 360
  return h
}

export function initialsOf(displayName: string, email: string): string {
  const source = displayName.trim() || email.split('@')[0]?.replace(/[._-]+/g, ' ') || '?'
  const parts = source.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

/**
 * A round avatar: the person's directory photo, or their tinted initials when there is none. The
 * photo comes through the backend (`GET /v1/directory/photo`, lib/person-photo.ts), never a Graph
 * token in the browser. No photo, an invited member who never signed in, a failed fetch and an
 * image the browser cannot decode all end as the same initials — never a broken image. Decorative
 * either way (aria-hidden, empty alt): the name beside it is the accessible label.
 */
export function Avatar({
  email,
  displayName,
  size = 28,
}: {
  email: string
  displayName: string
  size?: number
}) {
  const photo = usePersonPhoto(email)
  // An image the browser cannot decode falls back to initials rather than a broken-image icon.
  const [broken, setBroken] = useState<string | null>(null)
  if (photo && broken !== photo) {
    return (
      <img
        className="ppl-avatar ppl-avatar-photo"
        src={photo}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        style={{ width: size, height: size }}
        onError={() => setBroken(photo)}
      />
    )
  }
  const hue = hueFor(email)
  return (
    <span
      className="ppl-avatar"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        background: `hsl(${hue} 55% 92%)`,
        color: `hsl(${hue} 55% 30%)`,
        fontSize: Math.round(size * 0.4),
      }}
    >
      {initialsOf(displayName, email)}
    </span>
  )
}

/** Minimum characters before a lookup is worth making — matches the backend's own floor. */
const MIN_QUERY = 3
const DEBOUNCE_MS = 300

/** A deliberately permissive address check: the tenant, not this regex, decides who exists. */
function isWellFormedEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export interface PeoplePickerProps {
  /** The chosen address, or '' for none. The email IS the identity. */
  value: string
  /** Reports the chosen address; `displayName` is present only when it came from the directory. */
  onChange: (email: string, displayName?: string) => void
  placeholder?: string
  /** Corporate domain an address must end with to be accepted. '' accepts any well-formed one. */
  domain?: string
  /** Rendered as the input's accessible name. */
  label?: string
  autoFocus?: boolean
}

export function PeoplePicker({
  value,
  onChange,
  placeholder,
  domain = 'capgemini.com',
  label,
  autoFocus,
}: PeoplePickerProps) {
  const { t } = useLang()
  const [query, setQuery] = useState('')
  const [people, setPeople] = useState<DirectoryPerson[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [error, setError] = useState('')
  const [chosenName, setChosenName] = useState('')
  /** A lookup completed for the current query and found nobody. Distinct from "not searched yet". */
  const [noMatch, setNoMatch] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const errorId = useId()
  const hintId = useId()

  const accessibleLabel = label ?? t('ppl_label')

  // Debounced lookup. `cancelled` guards the classic out-of-order race: a slow response for "du"
  // must not overwrite the list for "dubois" that the member is already looking at.
  useEffect(() => {
    const q = query.trim()
    if (q.length < MIN_QUERY) {
      setPeople([])
      setOpen(false)
      setNoMatch(false)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      void api
        .directorySearch(q)
        .then((res) => {
          if (cancelled) return
          setPeople(res.people)
          setOpen(res.people.length > 0)
          setActive(res.people.length > 0 ? 0 : -1)
          setNoMatch(res.people.length === 0)
        })
        .catch(() => {
          // The endpoint is documented never to fail, but a network blip must not strand the
          // picker: the typed-address path below still works.
          if (!cancelled) {
            setPeople([])
            setOpen(false)
            setNoMatch(true)
          }
        })
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  const suffix = useMemo(() => (domain ? `@${domain.toLowerCase()}` : ''), [domain])

  function choose(person: DirectoryPerson): void {
    setChosenName(person.displayName)
    setQuery('')
    setPeople([])
    setOpen(false)
    setActive(-1)
    setError('')
    onChange(person.email, person.displayName)
  }

  /** Accept whatever is typed, if it can possibly be a colleague's address. */
  function commitTyped(): void {
    const typed = query.trim().toLowerCase()
    if (!typed) return
    if (!isWellFormedEmail(typed)) {
      setError(t('ppl_err_format'))
      return
    }
    if (suffix && !typed.endsWith(suffix)) {
      setError(t('ppl_err_domain').replace('{domain}', domain))
      return
    }
    setChosenName('')
    setQuery('')
    setError('')
    onChange(typed)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' && people.length > 0) {
      e.preventDefault()
      setOpen(true)
      setActive((i) => (i + 1) % people.length)
      return
    }
    if (e.key === 'ArrowUp' && people.length > 0) {
      e.preventDefault()
      setOpen(true)
      setActive((i) => (i - 1 + people.length) % people.length)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const person = open && active >= 0 ? people[active] : undefined
      // Enter on a highlighted suggestion picks it; Enter on free text accepts the address. That
      // second branch is what keeps the picker usable when the directory answers nothing.
      if (person) choose(person)
      else commitTyped()
      return
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      setOpen(false)
      setActive(-1)
    }
  }

  // ── Chip mode: someone is chosen ──────────────────────────────────────────
  if (value) {
    return (
      <span className="ppl-chip">
        <Avatar email={value} displayName={chosenName} size={22} />
        <span className="ppl-chip-name">{chosenName || value}</span>
        <button
          type="button"
          className="ppl-chip-clear"
          aria-label={t('ppl_clear').replace('{who}', chosenName || value)}
          onClick={() => {
            setChosenName('')
            onChange('')
            // Put focus back where the member can carry on typing a new name.
            queueMicrotask(() => inputRef.current?.focus())
          }}
        >
          <IconClose />
        </button>
      </span>
    )
  }

  // ── Search mode ───────────────────────────────────────────────────────────
  return (
    <div className="ppl">
      <input
        ref={inputRef}
        type="text"
        className="ppl-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={accessibleLabel}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-describedby={error ? errorId : noMatch ? hintId : undefined}
        aria-invalid={error ? true : undefined}
        placeholder={placeholder ?? t('ppl_placeholder')}
        value={query}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQuery(e.target.value)
          setError('')
          setNoMatch(false)
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      />

      <ul
        id={listId}
        role="listbox"
        aria-label={accessibleLabel}
        className={`ppl-list${open ? '' : ' ppl-list-hidden'}`}
      >
        {open &&
          people.map((p, i) => (
            <li
              key={p.email}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`ppl-option${i === active ? ' is-active' : ''}`}
              // onMouseDown, not onClick: the input's blur fires first and would close the list
              // before a click ever lands.
              onMouseDown={(e) => {
                e.preventDefault()
                choose(p)
              }}
              onMouseEnter={() => setActive(i)}
            >
              <Avatar email={p.email} displayName={p.displayName} size={24} />
              <span className="ppl-option-name">{p.displayName}</span>
              <span className="ppl-option-mail">{p.email}</span>
            </li>
          ))}
      </ul>

      {error && (
        <p id={errorId} className="ppl-error" role="alert">
          {error}
        </p>
      )}

      {/* An empty directory is a SUPPORTED state, not a failure: the lookup needs a Graph
          permission that may never be consented, so the typed-address path is the real fallback.
          Rendering nothing at all made that fallback invisible and the control look broken. */}
      {!error && noMatch && (
        <p id={hintId} className="ppl-hint">
          {t('ppl_no_match').replace('{domain}', domain || 'capgemini.com')}
        </p>
      )}

      {/* aria-describedby alone is NOT enough here: a description is resolved when focus ARRIVES,
          and focus is already parked in this input and never moves. The hint would appear silently
          mid-typing — the exact silence it was written to end. This live region is ALWAYS mounted
          (a role="status" added at the same moment as its text is unreliably announced) and only
          its content changes, which is what makes the update speak. */}
      <p className="visually-hidden" role="status">
        {!error && noMatch ? t('ppl_no_match').replace('{domain}', domain || 'capgemini.com') : ''}
      </p>
    </div>
  )
}
